/**
 * FORGE-161: custom workflow steps engine (portal mirror of Forge's
 * src/orchestrator/shared/customSteps.ts).
 *
 * Byte-for-byte logical mirror of the orchestrator module. The portal Workflow
 * editor validates `custom_agents` + `workflow.custom_steps` with the SAME rules
 * the orchestrator enforces in loadRepoConfig, so what the operator sees as valid
 * in the UI is exactly what the pipeline will run. If you edit one, edit BOTH.
 *
 * Pure + dependency-free.
 */

/** Stages a custom step may fire after (the completion of a built-in agent). */
export const TRIGGER_STAGES = ['backend', 'web', 'mobile', 'qa', 'devops'] as const;
export type TriggerStage = (typeof TRIGGER_STAGES)[number];

/** Built-in agent names a custom agent must NOT shadow. */
const RESERVED_AGENT_NAMES = new Set([
  'epic-planner', 'planner-review', 'solution-architect', 'qa', 'qa-validate',
  'backend', 'web', 'mobile', 'solo', 'bugfix', 'secops', 'devops', 'devops-fix',
  'backend-review', 'web-review', 'mobile-review', 'devops-review', 'docs-agent',
  'retry-analyst', 'graph-builder', 'init', 'bootstrap-planner',
]);

const NAME_RE = /^[a-z][a-z0-9-]{1,40}$/; // kebab-case identifier, agent-type-safe

export interface CustomAgentDef {
  name: string;
  prompt: string;
  model?: string;
  max_iterations?: number;
}
export interface WorkflowCustomStep {
  after: string;
  agent: string;
  /**
   * FORGE-161 (verdict routing): true → a fail verdict / crash HALTS the story
   * (retry then BLOCK). false/unset → advisory (recorded, never blocks).
   */
  blocking?: boolean;
}

/** A custom step resolved to its agent definition, ready to launch. */
export interface ResolvedCustomStep {
  name: string;
  after: TriggerStage;
  prompt: string;
  model?: string;
  maxIterations?: number;
  blocking: boolean;
}

/**
 * Validate the custom-agents + custom-steps config. Returns human-readable
 * errors ([] when valid). Mirrors validateModelsConfig's zero-out-on-error
 * contract on the orchestrator; here it gates the editor's Save button.
 */
export function validateCustomSteps(
  customAgents: CustomAgentDef[] = [],
  customSteps: WorkflowCustomStep[] = [],
): string[] {
  const errs: string[] = [];
  const names = new Set<string>();

  for (const a of customAgents) {
    if (!a || typeof a.name !== 'string' || !NAME_RE.test(a.name)) {
      errs.push(`custom_agents: invalid name "${a?.name ?? ''}" — use kebab-case (a-z, 0-9, -), 2–41 chars`);
      continue;
    }
    if (RESERVED_AGENT_NAMES.has(a.name)) {
      errs.push(`custom_agents: "${a.name}" shadows a built-in agent — pick a different name`);
    }
    if (names.has(a.name)) errs.push(`custom_agents: duplicate name "${a.name}"`);
    names.add(a.name);
    if (typeof a.prompt !== 'string' || !a.prompt.trim()) {
      errs.push(`custom_agents: "${a.name}" requires a non-empty prompt path`);
    }
    if (a.max_iterations != null && (!Number.isInteger(a.max_iterations) || a.max_iterations < 1 || a.max_iterations > 120)) {
      errs.push(`custom_agents: "${a.name}" max_iterations must be an integer 1–120`);
    }
  }

  for (const s of customSteps) {
    if (!s || !(TRIGGER_STAGES as readonly string[]).includes(s.after)) {
      errs.push(`workflow.custom_steps: invalid "after: ${s?.after ?? ''}" — must be one of ${TRIGGER_STAGES.join(', ')}`);
    }
    if (!s || typeof s.agent !== 'string' || !names.has(s.agent)) {
      errs.push(`workflow.custom_steps: "${s?.agent ?? ''}" is not defined in custom_agents`);
    }
  }
  return errs;
}

/** Resolve the custom steps that fire after a given stage, in declared order. */
export function stepsAfter(
  stage: string,
  customSteps: WorkflowCustomStep[] = [],
  customAgents: CustomAgentDef[] = [],
): ResolvedCustomStep[] {
  const byName = new Map(customAgents.map((a) => [a.name, a]));
  const out: ResolvedCustomStep[] = [];
  for (const s of customSteps) {
    if (s.after !== stage) continue;
    const def = byName.get(s.agent);
    if (!def) continue;
    out.push({
      name: def.name,
      after: stage as TriggerStage,
      prompt: def.prompt,
      model: def.model,
      maxIterations: def.max_iterations,
      blocking: s.blocking === true,
    });
  }
  return out;
}

/** Is this name a reserved built-in agent? (Exposed for editor inline hints.) */
export function isReservedAgentName(name: string): boolean {
  return RESERVED_AGENT_NAMES.has(name);
}
